/**
 * 极练 Rehab & Fit — 科学代谢与训练能量消耗引擎。
 *
 * 理论依据与科学标准：
 * 1. ACSM（美国运动医学会）抗阻训练能量消耗标准（Compendium of Physical Activities，抗阻中高强度 MET = 5.5）；
 * 2. EPOC（Excess Post-exercise Oxygen Consumption，运动后过量氧耗 / 后燃效应）：
 *    抗阻训练破坏肌纤维结构并消耗肌糖原，在接下来的 14–24 小时内肌蛋白修复与代谢提升产生约 7%–10% 的额外消耗（默认按 8% 保守计算）；
 * 3. 7700 kcal 能量平衡定律：每产生 7700 kcal 的净热量缺口约等于氧化消耗 1 kg 纯脂肪组织；
 * 4. Schoenfeld 肌肉肥大有效做功理论：RIR ≤ 3 的组数能够完全激活高阈值运动单位（High-Threshold Motor Units），是促进肌肥大与维持瘦体重的有效刺激组。
 */

import type { WeightUnitScope } from "@jilian/shared";
import { RULES_VERSION } from "@jilian/shared";

export interface SetPerformance {
  weightKg: number | null;
  reps: number | null;
  rir: number | null;
  completed: boolean;
  isCompound: boolean;
  primaryMuscles: string[];
}

export interface WorkoutExercisePerformance {
  exerciseId: string;
  nameZh: string;
  primaryMuscles: string[];
  compoundType: "compound" | "isolation";
  weightUnitScope: WeightUnitScope;
  sets: {
    weightKg: number | null;
    reps: number | null;
    rir: number | null;
    completed: boolean;
  }[];
}

export interface WorkoutMetabolismInput {
  bodyWeightKg: number;
  durationMinutes: number;
  exercises: WorkoutExercisePerformance[];
}

export interface MuscleStimulusSummary {
  muscle: string;
  effectiveSets: number;
  totalVolumeKg: number;
}

export interface WorkoutMetabolismResult {
  /** 练中直接代谢消耗（Active Burn，单位 kcal） */
  activeCalorieBurn: number;
  /** EPOC 运动后过量氧耗 / 24小时后燃（单位 kcal） */
  epocCalorieBurn: number;
  /** 单场训练总消耗（Active + EPOC，单位 kcal） */
  totalCalorieBurn: number;
  /** 相当于消耗的纯脂肪组织储备（克，每 7700 kcal ≈ 1000g 脂肪） */
  equivalentFatBurnGrams: number;
  /** 相当于中速慢跑（8 km/h）的等效时长（分钟） */
  equivalentJoggingMinutes: number;

  /** 高刺激有效增肌做功组数（RIR ≤ 3） */
  effectiveHypertrophySets: number;
  /** 训练总完成组数 */
  totalCompletedSets: number;
  /** 估算做功总容量（kg） */
  totalVolumeKg: number;
  /** 各肌群刺激分布 */
  muscleStimulus: MuscleStimulusSummary[];
  /** 48小时蛋白质合成建议 */
  proteinTargetGrams: number;
  /** 规则引擎版本 */
  ruleVersion: string;
}

export interface DailyEnergyBalanceInput {
  bodyWeightKg: number;
  heightCm?: number;
  age?: number;
  gender?: "male" | "female";
  /** 今日饮食总摄入热量（kcal） */
  intakeCalories: number;
  /** 今日饮食总蛋白质摄入（g） */
  intakeProteinGrams: number;
  /** 今日训练总消耗（来自 calculateWorkoutMetabolism，kcal；无训练则为 0） */
  workoutBurnCalories: number;
  /** 基础活动水平（非训练日常步数/活动，默认 1.25 轻体力） */
  baselineActivityFactor?: number;
}

export interface DailyEnergyBalanceResult {
  /** 基础代谢率 BMR（Mifflin-St Jeor 公式） */
  bmr: number;
  /** 今日全天总能量消耗 TDEE（BMR * 1.25 + 训练总消耗） */
  tdee: number;
  /** 今日热量总摄入 */
  intakeCalories: number;
  /** 净能量平衡（正为缺口 Deficit，负为盈余 Surplus，单位 kcal） */
  netDeficitCalories: number;
  /** 今日减脂/增肌状态描述 */
  status: "fat_loss" | "muscle_gain_surplus" | "maintenance";
  /** 预计今日对脂肪组织的净改变贡献（克，负数代表减脂，正数代表储脂） */
  projectedFatChangeGrams: number;
  /** 按此节奏 30 天预计体脂/体重变化（kg，负数代表下降） */
  projected30DayChangeKg: number;
  /** 蛋白质达成率（%） */
  proteinAchievementPercent: number;
  /** 科学建议文本 */
  adviceSummary: string;
}

/**
 * 1. 单场训练能量消耗与肌肉刺激度计算
 */
export function calculateWorkoutMetabolism(input: WorkoutMetabolismInput): WorkoutMetabolismResult {
  const { bodyWeightKg, durationMinutes, exercises } = input;
  const validWeight = bodyWeightKg > 0 ? bodyWeightKg : 75;
  const validDurationHours = Math.max(0, durationMinutes) / 60;

  // ACSM 抗阻标准：MET = 5.5
  const baseActiveBurn = 5.5 * validWeight * validDurationHours;

  let extraSetIntensityBurn = 0;
  let effectiveSetsCount = 0;
  let totalCompletedSets = 0;
  let totalVolumeKg = 0;
  const muscleMap = new Map<string, { effectiveSets: number; totalVolume: number }>();

  for (const ex of exercises) {
    const isCompound = ex.compoundType === "compound";
    for (const s of ex.sets) {
      if (!s.completed) continue;
      totalCompletedSets += 1;

      const rir = s.rir ?? 2;
      const reps = s.reps ?? 0;
      const weight = s.weightKg ?? 0;

      // 容量计算
      if (weight > 0 && reps > 0) {
        totalVolumeKg += weight * reps;
      }

      // 高刺激有效组判定：RIR ≤ 3
      const isEffective = rir <= 3;
      if (isEffective) {
        effectiveSetsCount += 1;
        // 复合动作高刺激组约额外耗能 7.5 kcal，孤立动作约 4.5 kcal
        extraSetIntensityBurn += isCompound ? 7.5 : 4.5;
      }

      // 肌群统计
      for (const m of ex.primaryMuscles) {
        const cur = muscleMap.get(m) ?? { effectiveSets: 0, totalVolume: 0 };
        if (isEffective) cur.effectiveSets += 1;
        if (weight > 0 && reps > 0) cur.totalVolume += weight * reps;
        muscleMap.set(m, cur);
      }
    }
  }

  // 练中直接代谢消耗
  const activeCalorieBurn = Math.round(baseActiveBurn + extraSetIntensityBurn);
  // EPOC 运动后过量氧耗（后燃效应，约占 8%）
  const epocCalorieBurn = Math.round(activeCalorieBurn * 0.08);
  // 单场总消耗
  const totalCalorieBurn = activeCalorieBurn + epocCalorieBurn;

  // 等效脂肪消耗（每 7700 kcal 纯脂肪）
  const equivalentFatBurnGrams = Math.round((totalCalorieBurn / 7700) * 1000 * 10) / 10;

  // 等效慢跑时间（8 km/h 慢跑 MET ≈ 8.3, 耗能 = 8.3 * kg * (t/60)）
  const jogRatePerMin = (8.3 * validWeight) / 60;
  const equivalentJoggingMinutes = Math.round(totalCalorieBurn / (jogRatePerMin || 1));

  // 肌群刺激汇总
  const muscleStimulus: MuscleStimulusSummary[] = Array.from(muscleMap.entries()).map(
    ([muscle, data]) => ({
      muscle,
      effectiveSets: data.effectiveSets,
      totalVolumeKg: Math.round(data.totalVolume),
    }),
  );

  // 48小时蛋白质合成建议：按 1.8g/kg
  const proteinTargetGrams = Math.round(validWeight * 1.8);

  return {
    activeCalorieBurn,
    epocCalorieBurn,
    totalCalorieBurn,
    equivalentFatBurnGrams,
    equivalentJoggingMinutes,
    effectiveHypertrophySets: effectiveSetsCount,
    totalCompletedSets,
    totalVolumeKg: Math.round(totalVolumeKg),
    muscleStimulus,
    proteinTargetGrams,
    ruleVersion: RULES_VERSION,
  };
}

/**
 * 2. 全天能量平衡与减脂增肌净成效计算
 */
export function calculateDailyEnergyBalance(input: DailyEnergyBalanceInput): DailyEnergyBalanceResult {
  const {
    bodyWeightKg,
    heightCm = 175,
    age = 30,
    gender = "male",
    intakeCalories,
    intakeProteinGrams,
    workoutBurnCalories,
    baselineActivityFactor = 1.25,
  } = input;

  const validWeight = bodyWeightKg > 0 ? bodyWeightKg : 75;

  // Mifflin-St Jeor 基础代谢公式 (kcal/day)
  let bmr = 10 * validWeight + 6.25 * heightCm - 5 * age;
  bmr += gender === "female" ? -161 : 5;
  bmr = Math.round(bmr);

  // 全天总能量消耗 TDEE
  const baselineExpenditure = Math.round(bmr * baselineActivityFactor);
  const tdee = baselineExpenditure + Math.round(workoutBurnCalories);

  // 净缺口（净消耗 - 摄入）
  const netDeficitCalories = tdee - intakeCalories;

  let status: DailyEnergyBalanceResult["status"] = "maintenance";
  if (netDeficitCalories >= 200) {
    status = "fat_loss";
  } else if (netDeficitCalories <= -200) {
    status = "muscle_gain_surplus";
  }

  // 脂肪改变克数（每 7700 kcal ≈ 1000g 脂肪；缺口为正 -> 减脂为负）
  const projectedFatChangeGrams = -Math.round((netDeficitCalories / 7700) * 1000 * 10) / 10;

  // 30 天预期变化（kg）
  const projected30DayChangeKg = -Math.round(((netDeficitCalories * 30) / 7700) * 10) / 10;

  // 蛋白质目标按 1.8g/kg
  const proteinTarget = validWeight * 1.8;
  const proteinAchievementPercent = Math.min(
    200,
    Math.round((intakeProteinGrams / (proteinTarget || 1)) * 100),
  );

  let adviceSummary = "";
  if (status === "fat_loss") {
    adviceSummary = `今日创造了约 ${netDeficitCalories} kcal 的热量缺口，相当于减少约 ${Math.abs(projectedFatChangeGrams)} g 脂肪储备。保持此节奏可稳定高效减脂，且抗阻训练能最大程度保护瘦体重。`;
  } else if (status === "muscle_gain_surplus") {
    adviceSummary = `今日热量盈余约 ${Math.abs(netDeficitCalories)} kcal，适合高强度训练后的肌肉超量恢复与力量增长。请确保充足蛋白质摄入，配合双进阶训练将盈余转化为肌肉。`;
  } else {
    adviceSummary = `今日摄入与消耗基本平衡（能量处于维持期），适合巩固当前体重并持续改善身体成分（增肌塑形）。`;
  }

  return {
    bmr,
    tdee,
    intakeCalories,
    netDeficitCalories,
    status,
    projectedFatChangeGrams,
    projected30DayChangeKg,
    proteinAchievementPercent,
    adviceSummary,
  };
}
